import { useEffect, useRef, useState } from 'react';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import { Box, InputLabel } from '@mui/material';
import { DesktopTimePicker } from '@mui/x-date-pickers/DesktopTimePicker';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';

import BaseFieldContainer from '../common/baseFieldContainer';
import { buildDateTimePickerInputLabelSx } from '../fDateTimePicker/utils';
import {
  TIME_PICKER_ACTIONS,
  TIME_PICKER_ACTIONS_WITH_NOW,
  TIME_PICKER_DEFAULT_PLACEHOLDER,
  TIME_PICKER_DEFAULT_TIME_STEPS,
  TIME_PICKER_LOCALE_TEXT,
  TIME_PICKER_SINGLE_COLUMN_THRESHOLD,
} from './constants';
import {
  buildTimePickerPlaceholderSx,
  buildTimePickerTextFieldSx,
  buildTimePickerOptionSx,
  TIME_PICKER_POPPER_SX,
} from './styles';
import { attachTimePickerColumnControls, createTimePickerViewRenderer } from './utils';

import type { FTimePickerChangeContext, FTimePickerProps } from './types';
import type { Moment } from 'moment';

export default function FTimePicker({
  'data-testid': dataTestId,
  allowClear,
  ampm,
  disabledTime,
  disabledMeridiem = [],
  format,
  id,
  label,
  helperText,
  onAccept,
  onBlur,
  onChange,
  onClose,
  onOk,
  onOpen,
  onSelect,
  open,
  outlineColor,
  placeholder,
  required,
  showRequiredIndicator,
  disabled,
  fullWidth,
  error = false,
  showClockIcon = false,
  showNow = false,
  style,
  use12Hours,
  value,
  shouldDisableTime,
  timeSteps,
  ...props
}: FTimePickerProps) {
  const [isPickerOpen, setIsPickerOpen] = useState(false);
  const paperRef = useRef<HTMLDivElement | null>(null);
  const selectionViewRef = useRef<FTimePickerChangeContext['view']>(undefined);
  const clockViewRenderer = createTimePickerViewRenderer((view) => {
    selectionViewRef.current = view;
  });
  const isOpen = open ?? isPickerOpen;
  const previewPlaceholder = placeholder ?? TIME_PICKER_DEFAULT_PLACEHOLDER;
  const showPreviewPlaceholder = value == null && !isOpen;
  const handleChange = (
    nextValue: Moment | null,
    context: FTimePickerChangeContext = { validationError: null },
  ) => {
    if (selectionViewRef.current === undefined && context.validationError !== null) {
      onChange?.(null, context);
      onSelect?.(null);
      return;
    }
    onChange?.(nextValue, { ...context, view: selectionViewRef.current });
    onSelect?.(nextValue);
  };
  const handleAccept = (nextValue: Moment | null) => {
    onAccept?.(nextValue);
    if (nextValue != null) {
      onOk?.(nextValue);
    }
  };
  const handleShouldDisableTime: FTimePickerProps['shouldDisableTime'] = (timeValue, view) => {
    if (shouldDisableTime?.(timeValue, view)) {
      return true;
    }

    if (disabledTime == null) {
      return false;
    }

    const disabledTimeConfig = disabledTime(timeValue);

    if (view === 'hours') {
      return disabledTimeConfig.disabledHours?.().includes(timeValue.hour()) ?? false;
    }

    if (view === 'minutes') {
      return (
        disabledTimeConfig.disabledMinutes?.(timeValue.hour()).includes(timeValue.minute()) ??
        false
      );
    }

    return (
      disabledTimeConfig
        .disabledSeconds?.(timeValue.hour(), timeValue.minute())
        .includes(timeValue.second()) ?? false
    );
  };
  const handleOpen = () => {
    setIsPickerOpen(true);
    onOpen?.();
  };
  const handleClose = () => {
    setIsPickerOpen(false);
    onClose?.();
  };
  const handleTextFieldClick = () => {
    if (!disabled) {
      handleOpen();
    }
  };

  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }

    let cleanupColumnControls: (() => void) | undefined;
    const animationFrame = window.requestAnimationFrame(() => {
      cleanupColumnControls = attachTimePickerColumnControls(paperRef.current);
    });

    return () => {
      window.cancelAnimationFrame(animationFrame);
      cleanupColumnControls?.();
    };
  }, [isOpen]);

  return (
    <BaseFieldContainer
      required={required}
      showRequiredIndicator={showRequiredIndicator}
      disabled={disabled}
      helperText={helperText}
      error={error}
      fullWidth={fullWidth}
      alwaysRenderContainer
    >
      {label != null ? (
        <InputLabel
          disabled={disabled}
          error={error}
          required={required && !disabled ? false : required}
          shrink
          htmlFor={id}
          sx={buildDateTimePickerInputLabelSx(undefined, false)}
        >
          {label}
        </InputLabel>
      ) : null}
      <LocalizationProvider dateAdapter={AdapterMoment}>
        <DesktopTimePicker
          {...props}
          ampm={ampm ?? use12Hours}
          closeOnSelect={false}
          disabled={disabled}
          format={format}
          localeText={TIME_PICKER_LOCALE_TEXT}
          shouldDisableTime={handleShouldDisableTime}
          viewRenderers={{
            hours: clockViewRenderer,
            minutes: clockViewRenderer,
            seconds: clockViewRenderer,
            ...props.viewRenderers,
          }}
          thresholdToRenderTimeInASingleColumn={TIME_PICKER_SINGLE_COLUMN_THRESHOLD}
          timeSteps={timeSteps ?? TIME_PICKER_DEFAULT_TIME_STEPS}
          value={value ?? null}
          open={isOpen}
          onAccept={handleAccept}
          onClose={handleClose}
          onChange={handleChange}
          onOpen={handleOpen}
          slots={{ openPickerIcon: AccessTimeIcon }}
          slotProps={{
            digitalClockSectionItem: {
              sx: buildTimePickerOptionSx(disabledMeridiem),
            },
            actionBar: {
              actions: showNow ? [...TIME_PICKER_ACTIONS_WITH_NOW] : [...TIME_PICKER_ACTIONS],
            },
            field: {
              clearable: Boolean(allowClear),
              id,
              onClear: () => handleChange(null),
            },
            popper: {
              sx: TIME_PICKER_POPPER_SX,
            },
            desktopPaper: {
              ref: paperRef,
            },
            textField: {
              error,
              fullWidth,
              id,
              inputProps: {
                'data-testid': dataTestId,
              },
              onBlur,
              onClick: handleTextFieldClick,
              placeholder,
              style,
              sx: buildTimePickerTextFieldSx({
                error,
                fullWidth: Boolean(fullWidth),
                disabled: Boolean(disabled),
                outlineColor,
                showClockIcon,
                showPreviewPlaceholder,
              }),
            },
          }}
        />
        {showPreviewPlaceholder ? (
          <Box
            component="span"
            sx={buildTimePickerPlaceholderSx({
              disabled: Boolean(disabled),
              showClockIcon,
            })}
          >
            {previewPlaceholder}
          </Box>
        ) : null}
      </LocalizationProvider>
    </BaseFieldContainer>
  );
}
